import { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api, { isStaging } from '../lib/api';
import {
  AlertTriangle, RefreshCw, Plus, Pencil, Check, X, Search,
  ChevronDown, ChevronRight, Building2, Trash2,
  Wifi, Link2, RotateCcw, CheckCircle, ArrowRight, Wrench,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';

// ─── Apufunktiot ──────────────────────────────────────────────────────────────

export function getLocale(lang) {
  return lang === 'en' ? 'en-GB' : 'fi-FI';
}

// Huoltotila (#44): suojatilan hälytykset ovat hiljaa huollon päättymiseen asti.
// Näytetään kaikille rooleille, jotta asiakaskin tietää, ettei hälytyksiä tule.
export function MaintenanceBadge({ shelter, t, i18n }) {
  if (!shelter.maintenance) return null;
  const until = new Date(shelter.maintenance.ends_at).toLocaleTimeString(getLocale(i18n.language), { hour: '2-digit', minute: '2-digit' });
  return (
    <span
      className="inline-flex items-center gap-1 text-xs font-medium text-amber-800 bg-amber-100 rounded px-1.5 py-0.5 whitespace-nowrap"
      title={t('maintenanceMode.badgeTitle', { until })}
    >
      <Wrench size={11} /> {t('maintenanceMode.badge')} {until}
    </span>
  );
}

// Huoltokirjaus (#82): viimeisimmän huollon tila NOT näkyy Superadminin koontinäytöllä
// (päätös 12). Backend antaa service_status-kentän vain Superadminille.
export function ServiceNotBadge({ shelter, t }) {
  if (shelter.service_status !== 'not') return null;
  return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-800 bg-red-100 rounded px-1.5 py-0.5 whitespace-nowrap"
      title={t('service.notBadgeTitle')}>
      <Wrench size={11} /> {t('service.notBadge')}
    </span>
  );
}

// Muotoilee kuinka kauan suojatila on ollut offline (esim. "45 min", "3 h", "2 pv")
function formatOfflineDuration(lastSeen) {
  if (!lastSeen) return null;
  const minutes = Math.floor((Date.now() - new Date(lastSeen).getTime()) / 60_000);
  if (minutes < 1) return null;
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h`;
  const days = Math.floor(hours / 24);
  return `${days} pv`;
}

// ─── InlineEdit ───────────────────────────────────────────────────────────────

function InlineEdit({ value, onSave, onCancel, placeholder }) {
  const [val, setVal] = useState(value || '');
  return (
    <div className="flex items-center gap-1 mt-0.5">
      <input
        autoFocus
        value={val}
        onChange={e => setVal(e.target.value)}
        placeholder={placeholder}
        className="text-sm border border-gray-300 rounded px-1.5 py-0.5 w-40 focus:outline-none focus:border-brand-600"
        onKeyDown={e => { if (e.key === 'Enter') onSave(val); if (e.key === 'Escape') onCancel(); }}
      />
      <button onClick={() => onSave(val)} className="text-green-600 hover:text-green-800"><Check size={14} /></button>
      <button onClick={onCancel} className="text-gray-400 hover:text-gray-600"><X size={14} /></button>
    </div>
  );
}

// PoC 1.3 — LAN/WiFi/4G-ikonipillerit. Tila näkyy pillerin TÄYTEVÄRISTÄ suoraan
// (vihreä/punainen/harmaa) — ei title-hover-tekstin varassa, koska sovellus on
// mobiili-first eikä kosketusnäytöllä ole hoverointia (ks. CLAUDE.md). Aktiivinen
// yhteys saa sinisen reunuksen + pienen valkoisen pisteen, molemmat aina näkyvissä.
const CONNECTION_CHIP_STYLE = {
  up:             'bg-green-500 text-white',
  down:           'bg-red-500 text-white',
  not_configured: 'bg-gray-100 text-gray-400',
};

function ConnectionChip({ label, status, active, t }) {
  const style = CONNECTION_CHIP_STYLE[status] || CONNECTION_CHIP_STYLE.not_configured;
  const statusLabel =
    status === 'up' ? t('dashboard.list.connectionUp', 'Käytettävissä') :
    status === 'down' ? t('dashboard.list.connectionDown', 'Ei yhteyttä') :
    t('dashboard.list.connectionNotConfigured', 'Ei käytössä');

  return (
    <span
      aria-label={`${label}: ${statusLabel}${active ? ` — ${t('dashboard.list.connectionActive', 'Aktiivinen')}` : ''}`}
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${style} ${active ? 'ring-2 ring-offset-1 ring-brand-600' : ''}`}
    >
      {active && <span className="w-1.5 h-1.5 rounded-full bg-white shrink-0" />}
      {label}
    </span>
  );
}

export function ConnectionIndicators({ shelter, t }) {
  const { lan_status, wifi_status, mobile_status, active_connection } = shelter;
  // Löydös 17.9.2026 (kenttätesti): kun laite on Offline, connections.*-kentät
  // näyttävät yhä viimeisimmän heartbeatin arvon (esim. "WiFi vihreä") vaikka
  // laitteessa ei tällä hetkellä ole edes virtaa — harhaanjohtavaa. "Laite"-
  // sarake kertoo jo Offline-tilan erikseen, joten Yhteydet näytetään tässä
  // tilassa kaikki punaisina ("ei yhteyttä") sen sijaan että näytettäisiin
  // vanhentunut tallennettu tila — käyttäjän päätös 17.9.2026: selkeämpi kuin
  // harmaa "ei tietoa", koska mikään yhteys ei juuri nyt tosiasiallisesti toimita dataa.
  // Korjaus 22.9.2026: sama sääntö koskee myös suojatiloja joilla ei ole koskaan
  // ollut mitään yhteystietoa (esim. ei linkitettyä laitetta lainkaan, shelter.online
  // on tällöin falsy) — aiempi erillinen "Ei tietoa" -tekstihaara harhaanjohti
  // näyttämällä yhden harmaan tekstin kolmen selkeän punaisen merkin sijaan.
  const displayLan    = shelter.online ? lan_status    : 'down';
  const displayWifi   = shelter.online ? wifi_status   : 'down';
  const displayMobile = shelter.online ? mobile_status : 'down';
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <ConnectionChip label={t('dashboard.list.connectionLan', 'Ethernet')} status={displayLan} active={shelter.online && active_connection === 'lan'} t={t} />
      <ConnectionChip label={t('dashboard.list.connectionWifi', 'WiFi')} status={displayWifi} active={shelter.online && active_connection === 'wifi'} t={t} />
      <ConnectionChip label={t('dashboard.list.connectionMobile', 'SIM')} status={displayMobile} active={shelter.online && active_connection === '4g'} t={t} />
    </div>
  );
}

// ─── ShelterListRow ───────────────────────────────────────────────────────────

// PoC 1.3 — sama Yhteydet-data tulkitaan tässä myös "varoitukseksi": jokin
// yhteystyyppi punaisena vaikka laite on muuten online (PoC 7 -päätös 15.9.2026,
// ks. spec). Ei uutta dataa, vain uusi tulkinta olemassa olevasta.
export function hasConnectionWarning(shelter) {
  if (!shelter.online) return false;
  return ['lan_status', 'wifi_status', 'mobile_status'].some(k => shelter[k] === 'down');
}

// Laite-sarakkeen teksti. #7: linkitetty laite, joka ei ole koskaan lähettänyt
// heartbeatia (backendin never_connected, 10 min linkityksestä tai tunnusten hausta),
// on Offline-tilan erikoistapaus: väri sama, teksti kertoo, ettei yhteyttä ole ollut.
export function deviceStatusLabel(shelter, t) {
  if (shelter.online) return t('sensors.online');
  if (shelter.never_connected) return t('sensors.neverConnected', 'Ei koskaan yhdistänyt');
  return t('sensors.offline');
}

// Verkkovirran "Testaamatta"-tila (22.9.2026) — DIN1-dry-contact-tulo ei voi
// ohjelmallisesti erottaa "ei kytketty mitään" -tilaa yhdestä kahdesta
// kelvollisesta lukemasta (molemmat lepäävät samassa arvossa, ks. spec Epic
// 2.8 / Pre-shipment-kohta 3). Backend (mqtt.js) merkitsee power_verified
// true:ksi vasta kun arvo on aidosti muuttunut ensimmäisestä lukemasta —
// tämä todistaa linjan oikeasti reagoivan eikä vain lepäävän kiinteässä tilassa.
export function getPowerStatus(shelter) {
  const value = shelter.latest?.power?.value;
  if (value == null) return 'unknown';
  if (!shelter.power_verified) return 'untested';
  return value === 1 ? 'ok' : 'fail';
}

export function ShelterListRow({ shelter, unackedIds, onReload, canEdit, onDelete }) {
  const { t, i18n } = useTranslation();
  const hasAlert = unackedIds.has(shelter.id);
  const offlineDuration = !shelter.online ? formatOfflineDuration(shelter.last_seen) : null;
  const warning = hasConnectionWarning(shelter);

  const [editingName, setEditingName] = useState(false);

  async function saveName(name) {
    await api.patch(`/api/shelters/${shelter.id}`, { name });
    onReload();
  }

  const prevNames = (shelter.name_history_7d || []).filter(h => h.old_name && h.old_name !== shelter.name);

  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
      <td className="py-2 px-3">
        {editingName ? (
          <InlineEdit
            value={shelter.name}
            placeholder={t('dashboard.card.namePlaceholder')}
            onSave={v => { setEditingName(false); saveName(v); }}
            onCancel={() => setEditingName(false)}
          />
        ) : (
          <div className="flex items-center gap-1 group">
            <Link to={`/suojatila/${shelter.id}`} className="font-medium text-gray-900 text-sm hover:text-brand-700 hover:underline">
              {shelter.name}
            </Link>
            {hasAlert && <AlertTriangle size={12} className="text-red-500 shrink-0" />}
            {canEdit && (
              <button
                onClick={() => setEditingName(true)}
                className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-brand-700 transition-opacity"
              >
                <Pencil size={11} />
              </button>
            )}
          </div>
        )}
        {prevNames.length > 0 && (
          <div className="mt-1 space-y-0.5">
            {prevNames.map((h, i) => (
              <div key={i} className="text-xs text-amber-600 bg-amber-50 rounded px-1.5 py-0.5">
                {t('dashboard.card.prevName')}: <span className="font-medium">{h.old_name}</span>
              </div>
            ))}
          </div>
        )}
      </td>
      <td className="py-2 px-3 whitespace-nowrap">
        {(() => {
          const powerStatus = getPowerStatus(shelter);
          if (powerStatus === 'ok') {
            return (
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full shrink-0 bg-green-400" />
                <span className="text-xs font-medium text-green-600">{t('sensors.powerOk', 'OK')}</span>
              </div>
            );
          }
          if (powerStatus === 'fail') {
            return (
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full shrink-0 bg-red-400" />
                <span className="text-xs font-medium text-red-600">{t('sensors.powerFail', 'Poikki')}</span>
              </div>
            );
          }
          if (powerStatus === 'untested') {
            return (
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full shrink-0 bg-amber-400" />
                <span className="text-xs font-medium text-amber-600">{t('sensors.powerUntested', 'Testaamatta')}</span>
              </div>
            );
          }
          return <span className="text-xs text-gray-400">{t('sensors.powerUnknown', 'Ei tietoa')}</span>;
        })()}
      </td>
      <td className="py-2 px-3 whitespace-nowrap">
        <div className="flex items-center gap-1.5">
          <span className={`w-2 h-2 rounded-full shrink-0 ${shelter.online ? 'bg-green-400' : 'bg-red-400'}`} />
          <span className={`text-xs font-medium ${shelter.online ? 'text-green-600' : 'text-red-600'}`}>
            {deviceStatusLabel(shelter, t)}
          </span>
          {hasAlert && <AlertTriangle size={12} className="text-red-500" />}
        </div>
        {offlineDuration && (
          <span className="block text-xs text-red-500 mt-0.5">
            {t('dashboard.list.offlineSince', 'Offline')} {offlineDuration}
          </span>
        )}
        {shelter.maintenance && <span className="block mt-0.5"><MaintenanceBadge shelter={shelter} t={t} i18n={i18n} /></span>}
        {shelter.service_status === 'not' && <span className="block mt-0.5"><ServiceNotBadge shelter={shelter} t={t} /></span>}
      </td>
      <td className="py-2 px-3 whitespace-nowrap">
        <div className="flex items-center gap-1.5">
          <ConnectionIndicators shelter={shelter} t={t} />
          {warning && <AlertTriangle size={12} className="text-amber-500 shrink-0" aria-label={t("dashboard.list.connectionWarning")} />}
        </div>
      </td>
      <td className="py-2 px-3 text-xs text-gray-400 whitespace-nowrap">
        {shelter.last_seen
          ? new Date(shelter.last_seen).toLocaleTimeString(getLocale(i18n.language), { hour: '2-digit', minute: '2-digit' })
          : t('dashboard.list.noConnection')}
      </td>
      {onDelete && (
        <td className="py-2 px-3 text-right">
          <button
            onClick={() => onDelete(shelter.id)}
            className="text-gray-300 hover:text-red-600 transition-colors"
            title={t("dashboard.list.deleteShelter")}
          >
            <Trash2 size={13} />
          </button>
        </td>
      )}
    </tr>
  );
}

// Jaettu taulukon otsikko — käytetään sekä Adminin ShelterListView'ssä
// että Superadminin globaalissa koontinäkymässä (PoC 6/7) ja Asiakaskortilla.
export function ShelterTableHeader({ t, showDelete }) {
  return (
    <thead>
      <tr className="bg-gray-50 border-b border-gray-200">
        <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('dashboard.list.shelter')}</th>
        <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{t('sensors.power', 'Verkkovirta')}</th>
        <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{t('dashboard.list.device', 'Laite')}</th>
        <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{t('dashboard.list.connections', 'Yhteydet')}</th>
        <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{t('dashboard.list.updated')}</th>
        {showDelete && <th className="py-2 px-3"></th>}
      </tr>
    </thead>
  );
}

// ─── ShelterCardRow (mobiili) ───────────────────────────────────────────────────

// Kokeilu 21.9.2026: taulukko (7 saraketta) vaati vaakavierityksen kapealla
// näytöllä (overflow-x-auto) — käyttäjän mukaan huono skaalautuvuus mobiilissa.
// Alle `sm`-breakpointin (640px) näytetään tämän sijasta kaksiriviset kortit per
// suojatila, samalla datalla: rivi 1 = tunniste+sijainti, rivi 2 = elävä tila.
// Rivi 1/2-jako mukailee ShelterCardPage.js:n omaa Tila/Perustiedot-jakoa. Ei
// vaakavieritystä, flex-wrap taipuu tarvittaessa useammalle riville hyvin
// kapealla näytöllä. Taulukko säilyy koskemattomana `sm`:stä ylöspäin.
function ShelterCardRow({ shelter, unackedIds, onReload, canEdit, onDelete }) {
  const { t, i18n } = useTranslation();
  const hasAlert = unackedIds.has(shelter.id);
  const offlineDuration = !shelter.online ? formatOfflineDuration(shelter.last_seen) : null;
  const warning = hasConnectionWarning(shelter);
  const [editingName, setEditingName] = useState(false);
  const powerStatus = getPowerStatus(shelter);

  async function saveName(name) {
    await api.patch(`/api/shelters/${shelter.id}`, { name });
    onReload();
  }

  const prevNames = (shelter.name_history_7d || []).filter(h => h.old_name && h.old_name !== shelter.name);

  return (
    <div className="border-b border-gray-100 px-3 py-2.5 last:border-b-0">
      {/* Rivi 1 — suojatilan nimi */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-1.5">
        {editingName ? (
          <InlineEdit
            value={shelter.name}
            placeholder={t('dashboard.card.namePlaceholder')}
            onSave={v => { setEditingName(false); saveName(v); }}
            onCancel={() => setEditingName(false)}
          />
        ) : (
          <div className="flex items-center gap-1">
            <Link to={`/suojatila/${shelter.id}`} className="font-medium text-gray-900 text-sm hover:text-brand-700 hover:underline">
              {shelter.name}
            </Link>
            {hasAlert && <AlertTriangle size={12} className="text-red-500 shrink-0" />}
            {canEdit && (
              <button onClick={() => setEditingName(true)} className="text-gray-400 hover:text-brand-700">
                <Pencil size={10} />
              </button>
            )}
          </div>
        )}
        {prevNames.length > 0 && prevNames.map((h, i) => (
          <span key={i} className="text-xs text-amber-600 bg-amber-50 rounded px-1.5 py-0.5">
            {t('dashboard.card.prevName')}: <span className="font-medium">{h.old_name}</span>
          </span>
        ))}
      </div>

      {/* Rivi 2 — elävä tila */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        <span className="flex items-center gap-1">
          <span className="text-gray-400">{t('sensors.power', 'Verkkovirta')}:</span>
          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${powerStatus === 'ok' ? 'bg-green-400' : powerStatus === 'fail' ? 'bg-red-400' : powerStatus === 'untested' ? 'bg-amber-400' : 'bg-gray-300'}`} />
          <span className={`font-medium ${powerStatus === 'ok' ? 'text-green-600' : powerStatus === 'fail' ? 'text-red-600' : powerStatus === 'untested' ? 'text-amber-600' : 'text-gray-400'}`}>
            {powerStatus === 'ok' ? t('sensors.powerOk', 'OK') : powerStatus === 'fail' ? t('sensors.powerFail', 'Poikki') : powerStatus === 'untested' ? t('sensors.powerUntested', 'Testaamatta') : t('sensors.powerUnknown', 'Ei tietoa')}
          </span>
        </span>
        <span className="flex items-center gap-1">
          <span className="text-gray-400">{t('dashboard.list.device', 'Laite')}:</span>
          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${shelter.online ? 'bg-green-400' : 'bg-red-400'}`} />
          <span className={`font-medium ${shelter.online ? 'text-green-600' : 'text-red-600'}`}>
            {deviceStatusLabel(shelter, t)}
          </span>
          {offlineDuration && <span className="text-red-500">({offlineDuration})</span>}
          <MaintenanceBadge shelter={shelter} t={t} i18n={i18n} />
          <ServiceNotBadge shelter={shelter} t={t} />
        </span>
        <span className="flex items-center gap-1 flex-wrap">
          <span className="text-gray-400">{t('dashboard.list.connections', 'Yhteydet')}:</span>
          <ConnectionIndicators shelter={shelter} t={t} />
          {warning && <AlertTriangle size={11} className="text-amber-500 shrink-0" aria-label={t("dashboard.list.connectionWarning")} />}
        </span>
        <span className="text-gray-400 ml-auto whitespace-nowrap">
          {t('dashboard.list.updated', 'Päivitetty')}: {shelter.last_seen
            ? new Date(shelter.last_seen).toLocaleTimeString(getLocale(i18n.language), { hour: '2-digit', minute: '2-digit' })
            : t('dashboard.list.noConnection')}
        </span>
        {onDelete && (
          <button onClick={() => onDelete(shelter.id)} className="text-gray-300 hover:text-red-600" title={t("dashboard.list.deleteShelter")}>
            <Trash2 size={12} />
          </button>
        )}
      </div>
    </div>
  );
}

// ─── ShelterTable ────────────────────────────────────────────────────────────────

// Yhdistää taulukon (`sm`:stä ylöspäin) ja mobiilikortit (alle `sm`) yhdeksi
// komponentiksi — käytetään kaikissa neljässä suojatilalistauksessa (Adminin
// ryhmitelty/tasainen koontinäyttö, Superadminin globaali koontinäyttö,
// Asiakaskortti) jotka kaikki toistivat aiemmin saman overflow-x-auto+table-
// kuvion erikseen.
export function ShelterTable({ shelters, unackedIds, onReload, canEdit, onDelete }) {
  const { t } = useTranslation();
  return (
    <>
      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full">
          <ShelterTableHeader t={t} showDelete={!!onDelete} />
          <tbody>
            {shelters.map(s => (
              <ShelterListRow key={s.id} shelter={s} unackedIds={unackedIds} onReload={onReload} canEdit={canEdit} onDelete={onDelete} />
            ))}
          </tbody>
        </table>
      </div>
      <div className="sm:hidden">
        {shelters.map(s => (
          <ShelterCardRow key={s.id} shelter={s} unackedIds={unackedIds} onReload={onReload} canEdit={canEdit} onDelete={onDelete} />
        ))}
      </div>
    </>
  );
}

// ─── ShelterListView ──────────────────────────────────────────────────────────

function ShelterListView({ shelters, groups, selectedGroup, unackedIds, onReload, canEdit }) {
  const { t } = useTranslation();
  const [flatMode, setFlatMode] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState({});

  const showGrouped = !flatMode && selectedGroup === null && groups.length > 0;

  const grouped = useMemo(() => {
    if (!showGrouped) return null;
    const hasGroupData = shelters.some(s => s.group_ids && s.group_ids.length > 0);
    if (!hasGroupData) return null;

    const result = [];
    const usedShelterIds = new Set();

    for (const g of groups) {
      const gShelters = shelters.filter(s => s.group_ids && s.group_ids.includes(g.id));
      if (gShelters.length === 0) continue;
      result.push({ group: g, shelters: gShelters });
      gShelters.forEach(s => usedShelterIds.add(s.id));
    }

    const ungrouped = shelters.filter(s => !usedShelterIds.has(s.id));
    if (ungrouped.length > 0) {
      result.push({ group: { id: '__ungrouped__', name: t('dashboard.list.ungrouped') }, shelters: ungrouped });
    }

    return result;
  }, [showGrouped, groups, shelters, t]);

  function toggleGroup(gid) {
    setCollapsedGroups(prev => ({ ...prev, [gid]: !prev[gid] }));
  }

  const groupModeToggle = (
    <div className="flex items-center gap-2 mb-3">
      <button
        onClick={() => setFlatMode(false)}
        className={`text-xs px-2.5 py-1 rounded-full font-medium transition-colors ${!flatMode ? 'bg-brand-700 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
      >
        {t('dashboard.list.byGroup')}
      </button>
      <button
        onClick={() => setFlatMode(true)}
        className={`text-xs px-2.5 py-1 rounded-full font-medium transition-colors ${flatMode ? 'bg-brand-700 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
      >
        {t('dashboard.list.flatList')}
      </button>
    </div>
  );

  if (grouped) {
    return (
      <div className="space-y-3">
        {groupModeToggle}
        {grouped.map(({ group, shelters: gShelters }) => {
          const collapsed = !!collapsedGroups[group.id];
          return (
            <div key={group.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <button
                className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors"
                onClick={() => toggleGroup(group.id)}
              >
                <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                  {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                  {group.name}
                  <span className="text-xs font-normal text-gray-400">{t('dashboard.list.shelterCount', { count: gShelters.length })}</span>
                </span>
              </button>
              {!collapsed && (
                <ShelterTable shelters={gShelters} unackedIds={unackedIds} onReload={onReload} canEdit={canEdit} />
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {showGrouped && groupModeToggle}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <ShelterTable shelters={shelters} unackedIds={unackedIds} onReload={onReload} canEdit={canEdit} />
      </div>
    </div>
  );
}

// ─── 2-vaiheinen linkitysdialogi (PoC 7 — siirretty TenantsPage.js:stä 15.9.2026) ─
// Verkkoasetukset (WAN/WiFi/staattinen IP/VLAN/proxy) konfiguroidaan laitteeseen
// itseensä UCI:lla SafePron toimitiloissa ennen toimitusta (teltonika_setup.py) —
// ei sovelluksesta käsin. Aiempi 3-vaiheinen dialogi ja koko network_config-putki
// todettiin 11.8.2026 vaikutuksettomaksi (ks. spec), dialogi kavennettu 2 vaiheeseen.

function LinkDeviceModal({ device, tenants, allShelters, onProvisioned, onClose }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [selectedShelter, setSelectedShelter] = useState('');
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState('');

  const filteredShelters = selectedTenant
    ? allShelters.filter(s => s.tenant_id === selectedTenant)
    : [];

  const shelterName = allShelters.find(s => s.id === selectedShelter)?.name || '';
  const tenantName  = tenants.find(t => t.id === selectedTenant)?.name || '';

  async function handleLink() {
    setLinking(true); setError('');
    try {
      await api.post(`/api/superadmin/pending-devices/${device.imei}/provision`, {
        shelter_id: selectedShelter,
      });
      onProvisioned(device.imei);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || t('tenants.pending.linkFailed'));
    } finally {
      setLinking(false);
    }
  }

  const inp = 'w-full rounded-lg border border-gray-300 text-sm px-3 py-2 focus:outline-none focus:border-brand-500';
  const sel = `${inp} bg-white`;
  const lbl = 'block text-xs font-medium text-gray-600 mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 p-6" onClick={e => e.stopPropagation()}>

        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-base font-semibold text-gray-900">{t('tenants.pending.linkModalTitle')}</h2>
            <p className="text-xs text-gray-500 font-mono mt-0.5">{device.imei}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
        </div>

        <div className="flex items-center gap-2 mb-6 text-xs">
          {[1,2].map(s => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center font-semibold text-xs
                ${step === s ? 'bg-brand-700 text-white' : step > s ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-400'}`}>
                {step > s ? <CheckCircle size={12} /> : s}
              </div>
              <span className={step >= s ? 'text-gray-700' : 'text-gray-400'}>
                {s === 1 ? t('tenants.pending.stepShelter') : t('tenants.pending.stepConfirm')}
              </span>
              {s < 2 && <ArrowRight size={12} className="text-gray-300" />}
            </div>
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-4">
            <div>
              <label className={lbl}>{t('users.customerLabel')}</label>
              <select value={selectedTenant} onChange={e => { setSelectedTenant(e.target.value); setSelectedShelter(''); }} className={sel}>
                <option value="">{t('users.selectCustomer')}</option>
                {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label className={lbl}>{t('tenants.pending.shelterLabel')}</label>
              <select value={selectedShelter} onChange={e => setSelectedShelter(e.target.value)} disabled={!selectedTenant} className={sel}>
                <option value="">{t('tenants.pending.selectShelter')}</option>
                {filteredShelters.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div className="flex justify-end pt-2">
              <button
                onClick={() => setStep(2)}
                disabled={!selectedShelter}
                className="flex items-center gap-1.5 bg-brand-700 hover:bg-brand-900 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50 transition-colors"
              >
                {t('tenants.pending.next')} <ArrowRight size={14} />
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">{t('tenants.pending.deviceImei')}</span>
                <span className="font-mono text-gray-800">{device.imei}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">{t('users.customerLabel')}</span>
                <span className="text-gray-800">{tenantName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">{t('tenants.pending.shelterLabel')}</span>
                <span className="text-gray-800">{shelterName}</span>
              </div>
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}

            <div className="flex justify-between pt-2">
              <button onClick={() => setStep(1)} className="text-sm text-gray-500 hover:text-gray-800 px-3 py-2">← {t('common.back')}</button>
              <button
                onClick={handleLink}
                disabled={linking}
                className="flex items-center gap-1.5 bg-green-700 hover:bg-green-800 text-white text-sm font-medium px-5 py-2 rounded-lg disabled:opacity-60 transition-colors"
              >
                {linking ? <RotateCcw size={13} className="animate-spin" /> : <Link2 size={13} />}
                {t('tenants.pending.link')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Odottava laite -rivi ─────────────────────────────────────────────────────

function PendingDeviceRow({ device, tenants, allShelters, onProvisioned, onDismissed }) {
  const { t, i18n } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  const lastSeen = new Date(device.last_seen);
  const minutesAgo = Math.round((Date.now() - lastSeen.getTime()) / 60000);
  const locale = { fi: 'fi-FI', en: 'en-GB', sv: 'sv-SE', no: 'nb-NO', et: 'et-EE' }[i18n.language] || 'fi-FI';
  const lastSeenLabel = minutesAgo < 2
    ? t('tenants.pending.lastSeenJustNow')
    : minutesAgo < 60
      ? t('tenants.pending.lastSeenMinutes', { count: minutesAgo })
      : lastSeen.toLocaleString(locale);

  async function dismiss() {
    setDismissing(true);
    try {
      await api.delete(`/api/superadmin/pending-devices/${device.imei}`);
      onDismissed(device.imei);
    } finally {
      setDismissing(false);
    }
  }

  return (
    <>
      {modalOpen && (
        <LinkDeviceModal
          device={device}
          tenants={tenants}
          allShelters={allShelters}
          onProvisioned={onProvisioned}
          onClose={() => setModalOpen(false)}
        />
      )}
      <div className="border-b border-amber-100 last:border-0">
        <div className="flex items-center gap-3 px-4 py-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-mono font-semibold text-gray-900">{device.imei}</p>
            <p className="text-xs text-gray-500">
              {device.firmware_version ? `FW ${device.firmware_version} · ` : ''}
              {lastSeenLabel}
            </p>
          </div>
          <button
            onClick={() => setModalOpen(true)}
            className="flex items-center gap-1.5 text-sm bg-brand-700 text-white px-3 py-1.5 rounded-lg hover:bg-brand-800 transition-colors whitespace-nowrap"
          >
            <Link2 size={13} />
            {t('tenants.pending.link')}
          </button>
          <button
            onClick={dismiss}
            disabled={dismissing}
            className="text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
            title={t('tenants.pending.dismiss')}
          >
            {dismissing ? <RotateCcw size={14} className="animate-spin" /> : <Trash2 size={14} />}
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Uusi asiakas -lomake (kevyt, avataan napista) ─────────────────────────────

function CreateTenantForm({ onCreated, onClose }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: '', contact_email: '' });
  const [creating, setCreating] = useState(false);

  async function handleCreate(e) {
    e.preventDefault();
    setCreating(true);
    try {
      const { data } = await api.post('/api/superadmin/tenants', form);
      onCreated({ ...data, shelter_count: 0, user_count: 0 });
      onClose();
    } finally {
      setCreating(false);
    }
  }

  return (
    <form onSubmit={handleCreate} className="bg-white rounded-xl border border-gray-200 p-4 mb-4 flex flex-wrap gap-3 items-center">
      <input
        required
        autoFocus
        placeholder={t('tenants.companyName')}
        value={form.name}
        onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
        className="rounded-lg border border-gray-200 text-sm px-3 py-2 flex-1 min-w-[10rem] focus:outline-none focus:border-brand-500"
      />
      <input
        type="email"
        placeholder={t('tenants.contactEmail')}
        value={form.contact_email}
        onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))}
        className="rounded-lg border border-gray-200 text-sm px-3 py-2 w-56 focus:outline-none focus:border-brand-500"
      />
      <button
        type="submit"
        disabled={creating}
        className="bg-brand-700 hover:bg-brand-900 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors whitespace-nowrap"
      >
        {creating ? t('tenants.creating') : t('tenants.create')}
      </button>
      <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700">
        <X size={16} />
      </button>
    </form>
  );
}

// ─── SuperadminDashboard — PoC 7 (15.9.2026): yhdistetty hallintatyökalu ───────
// Korvaa PoC 6:n pelkän valvontanäkymän + entisen Asiakkaat-sivun (TenantsPage.js,
// poistettu). Odottavat laitteet, asiakkaat ja niiden suojatilat samalla sivulla,
// porautuminen asiakaskortille (/asiakas/:id) ja suojatilakortille (/suojatila/:id).

// manage = false: Huolto-rooli (#86) näkee saman asiakasryhmitellyn näkymän valituista
// asiakkaista ilman hallintatyökaluja (odottavat laitteet, asiakkaan luonti, asiakaskortti).
function SuperadminDashboard({ manage = true }) {
  const { t } = useTranslation();
  const [tenants, setTenants] = useState([]);
  const [shelters, setShelters] = useState([]);
  const [unacked, setUnacked] = useState([]);
  const [pendingDevices, setPendingDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingPending, setLoadingPending] = useState(manage);
  const [error, setError] = useState('');
  const [collapsedTenants, setCollapsedTenants] = useState({});
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [tenRes, shelRes, alertRes] = await Promise.all([
        api.get(manage ? '/api/superadmin/tenants' : '/api/service/tenants'),
        api.get('/api/shelters'),
        api.get('/api/alerts?unacknowledged=true&limit=100'),
      ]);
      setTenants(Array.isArray(tenRes.data) ? tenRes.data : []);
      setShelters(Array.isArray(shelRes.data) ? shelRes.data : []);
      const alerts = Array.isArray(alertRes.data) ? alertRes.data : [];
      setUnacked(alerts.filter(a => !a.acknowledged_at));
    } catch {
      setError(t('dashboard.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t, manage]);

  const loadPending = useCallback(async () => {
    if (!manage) { setLoadingPending(false); return; }
    setLoadingPending(true);
    try {
      const { data } = await api.get('/api/superadmin/pending-devices');
      setPendingDevices(data || []);
    } finally {
      setLoadingPending(false);
    }
  }, [manage]);

  useEffect(() => {
    load();
    loadPending();
    const interval = setInterval(load, 30_000);
    return () => clearInterval(interval);
  }, [load, loadPending]);

  function removePendingDevice(imei) {
    setPendingDevices(prev => prev.filter(d => d.imei !== imei));
  }

  async function seedTestData() {
    setSeeding(true);
    try {
      await api.post('/api/superadmin/seed-test-data');
      await load();
    } finally {
      setSeeding(false);
    }
  }

  const unackedIds = useMemo(() => new Set(unacked.map(a => a.shelter_id)), [unacked]);
  const criticalCount = unacked.filter(a => a.level === 'critical').length;

  // Vain huollettavat suojatilat (safe_digi false, #82 versio 10) eivät kuulu valvontaan:
  // asiakasrivillä näkyy vain niiden määrä, suojatilat ovat asiakaskortilla ja Huolto-sivulla.
  const sheltersByTenant = useMemo(() => {
    const map = new Map();
    for (const s of shelters) {
      if (s.safe_digi === false) continue;
      const list = map.get(s.tenant_id) || [];
      list.push(s);
      map.set(s.tenant_id, list);
    }
    return map;
  }, [shelters]);

  const serviceOnlyCount = useMemo(() => {
    const map = new Map();
    for (const s of shelters) if (s.safe_digi === false) map.set(s.tenant_id, (map.get(s.tenant_id) || 0) + 1);
    return map;
  }, [shelters]);

  function toggleTenant(id) {
    setCollapsedTenants(prev => ({ ...prev, [id]: !prev[id] }));
  }

  return (
    <div>
      {/* Kriittiset hälytykset -banneri — kaikki asiakkaat */}
      {criticalCount > 0 && (
        <div className="mb-4 bg-red-50 border border-red-300 rounded-xl px-4 py-3 flex items-start gap-3">
          <AlertTriangle size={18} className="text-red-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-red-800">
              {t('dashboard.alertBanner.unacked', { count: criticalCount })}
            </p>
            <p className="text-xs text-red-600 mt-0.5">{t('dashboard.alertBanner.check')}</p>
          </div>
          <Link to="/alerts" className="ml-auto text-xs font-medium text-red-700 hover:underline">
            {t('dashboard.alertBanner.show')}
          </Link>
        </div>
      )}

      {/* Odottavat laitteet -banneri — näytetään aina kun on laitteita */}
      {(loadingPending || pendingDevices.length > 0) && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl mb-4 overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-amber-200">
            <Wifi size={16} className="text-amber-600" />
            <h2 className="text-sm font-semibold text-amber-900">
              {t('tenants.pending.title')}
              {!loadingPending && ` (${pendingDevices.length})`}
            </h2>
            <span className="text-xs text-amber-600 ml-1">— {t('tenants.pending.subtitle')}</span>
            <button onClick={loadPending} className="ml-auto text-amber-600 hover:text-amber-800 transition-colors">
              <RotateCcw size={14} className={loadingPending ? 'animate-spin' : ''} />
            </button>
          </div>
          {loadingPending && <p className="text-xs text-amber-600 px-4 py-3">{t('tenants.pending.loading')}</p>}
          {!loadingPending && pendingDevices.map(d => (
            <PendingDeviceRow
              key={d.imei}
              device={d}
              tenants={tenants}
              allShelters={shelters}
              onProvisioned={removePendingDevice}
              onDismissed={removePendingDevice}
            />
          ))}
        </div>
      )}

      {/* Otsikko */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h1 className="text-xl font-bold text-gray-900">{t('dashboard.title')}</h1>
        <div className="flex items-center gap-2">
          {manage && isStaging && (
            <button
              onClick={seedTestData}
              disabled={seeding}
              title={t('dashboard.seedTestDataHint')}
              className="flex items-center gap-1.5 text-xs bg-amber-100 hover:bg-amber-200 text-amber-800 px-3 py-1.5 rounded-lg disabled:opacity-60 transition-colors"
            >
              {seeding ? <RotateCcw size={13} className="animate-spin" /> : <CheckCircle size={13} />}
              {t('dashboard.seedTestData')}
            </button>
          )}
          {manage && !showCreateForm && (
            <button
              onClick={() => setShowCreateForm(true)}
              className="flex items-center gap-1.5 text-sm bg-brand-700 hover:bg-brand-900 text-white px-3 py-1.5 rounded-lg transition-colors"
            >
              <Plus size={14} />{t('tenants.createCustomer')}
            </button>
          )}
          <button onClick={load} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800">
            <RefreshCw size={14} />
            {t('dashboard.refresh')}
          </button>
        </div>
      </div>

      {showCreateForm && (
        <CreateTenantForm
          onCreated={tenant => setTenants(ts => [...ts, tenant])}
          onClose={() => setShowCreateForm(false)}
        />
      )}

      {loading && <div className="text-center py-16 text-gray-400 text-sm">{t('dashboard.loading')}</div>}
      {error && <div className="text-center py-16 text-red-500 text-sm">{error}</div>}

      {!loading && !error && (
        tenants.length === 0 ? (
          <div className="text-center py-16 text-gray-400 text-sm bg-white rounded-xl border border-gray-200">
            {t('tenants.noCustomers')}
          </div>
        ) : (
          <div className="space-y-3">
            {tenants.map(tenant => {
              const tShelters = sheltersByTenant.get(tenant.id) || [];
              const collapsed = !!collapsedTenants[tenant.id];
              const tenantAlertCount = tShelters.filter(s => unackedIds.has(s.id)).length;
              const tenantWarningCount = tShelters.filter(hasConnectionWarning).length;
              return (
                <div key={tenant.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="w-full flex items-center gap-2 px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors">
                    <button
                      onClick={() => toggleTenant(tenant.id)}
                      className="text-gray-400 hover:text-gray-700 shrink-0"
                      title={collapsed ? t('dashboard.expand') : t('dashboard.collapse')}
                    >
                      {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                    </button>
                    <Building2 size={14} className="text-brand-600 shrink-0" />
                    {manage ? (
                      <Link
                        to={`/asiakas/${tenant.id}`}
                        className="text-sm font-semibold text-gray-700 hover:text-brand-700 hover:underline truncate"
                      >
                        {tenant.name}
                      </Link>
                    ) : (
                      <span className="text-sm font-semibold text-gray-700 truncate">{tenant.name}</span>
                    )}
                    <span className="text-xs font-normal text-gray-400 whitespace-nowrap">
                      {t('dashboard.list.shelterCount', { count: tShelters.length })}
                    </span>
                    {serviceOnlyCount.get(tenant.id) > 0 && (
                      <span className="text-xs font-normal text-gray-400 whitespace-nowrap">
                        · {t('dashboard.list.serviceOnlyCount', { count: serviceOnlyCount.get(tenant.id) })}
                      </span>
                    )}
                    {tenantAlertCount > 0 && (
                      <span className="flex items-center gap-1 text-xs font-medium text-red-600 bg-red-50 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                        <AlertTriangle size={11} /> {tenantAlertCount}
                      </span>
                    )}
                    {tenantWarningCount > 0 && (
                      <span className="flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                        <AlertTriangle size={11} /> {tenantWarningCount}
                      </span>
                    )}
                  </div>
                  {!collapsed && (
                    tShelters.length === 0 ? (
                      <p className="text-sm text-gray-400 px-4 py-4">{t('tenants.shelter.noShelters')}</p>
                    ) : (
                      <ShelterTable shelters={tShelters} unackedIds={unackedIds} onReload={load} canEdit={false} />
                    )
                  )}
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}

// ─── DashboardPage ────────────────────────────────────────────────────────────

export default function DashboardPage() {
  const { profile } = useAuth();

  if (profile?.role === 'superadmin') {
    return <SuperadminDashboard />;
  }
  if (profile?.role === 'huolto') {
    return <SuperadminDashboard manage={false} />;
  }

  return <ShelterDashboard />;
}

function ShelterDashboard() {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const [shelters, setShelters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [unacked, setUnacked] = useState([]);
  const [bannerSensorTypes, setBannerSensorTypes] = useState(null);
  const [query, setQuery] = useState('');
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupShelterIds, setGroupShelterIds] = useState(null);

  const effectiveRole = profile?.role;
  const canEdit = effectiveRole === 'admin' || effectiveRole === 'superadmin';

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [shelRes, alertRes, groupsRes, settingsRes] = await Promise.all([
        api.get('/api/shelters'),
        api.get('/api/alerts?unacknowledged=true&limit=50'),
        api.get('/api/groups').catch(() => ({ data: [] })),
        api.get('/api/alerts/tenant-settings').catch(() => ({ data: [] })),
      ]);
      setShelters(Array.isArray(shelRes.data) ? shelRes.data : []);
      const alerts = Array.isArray(alertRes.data) ? alertRes.data : [];
      setUnacked(alerts.filter(a => !a.acknowledged_at));
      setGroups(Array.isArray(groupsRes.data) ? groupsRes.data : []);
      const settings = Array.isArray(settingsRes.data) ? settingsRes.data : [];
      const bannerTypes = new Set(settings.filter(s => s.show_on_dashboard).map(s => s.sensor_type));
      setBannerSensorTypes(bannerTypes.size > 0 ? bannerTypes : null);
    } catch {
      setError(t('dashboard.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 30_000);
    return () => clearInterval(interval);
  }, [load]);

  useEffect(() => {
    if (selectedGroup === null) {
      setGroupShelterIds(null);
      return;
    }
    api.get(`/api/groups/${selectedGroup}/shelters`)
      .then(({ data }) => {
        const ids = new Set((Array.isArray(data) ? data : []).map(s => s.id));
        setGroupShelterIds(ids);
      })
      .catch(() => setGroupShelterIds(new Set()));
  }, [selectedGroup]);

  const filteredShelters = shelters.filter(s => {
    if (groupShelterIds !== null && !groupShelterIds.has(s.id)) return false;
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    const haystack = [s.name, s.location, s.tenant_name].filter(Boolean).join(' ').toLowerCase();
    return haystack.includes(q);
  });

  const unackedIds = useMemo(() => new Set(unacked.map(a => a.shelter_id)), [unacked]);
  const criticalCount = unacked.filter(a =>
    a.level === 'critical' && (bannerSensorTypes === null || bannerSensorTypes.has(a.sensor_type))
  ).length;

  return (
    <div>
      {/* Kriittiset hälytykset -banneri */}
      {criticalCount > 0 && (
        <div className="mb-4 bg-red-50 border border-red-300 rounded-xl px-4 py-3 flex items-start gap-3">
          <AlertTriangle size={18} className="text-red-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-red-800">
              {t('dashboard.alertBanner.unacked', { count: criticalCount })}
            </p>
            <p className="text-xs text-red-600 mt-0.5">{t('dashboard.alertBanner.check')}</p>
          </div>
          <Link to="/alerts" className="ml-auto text-xs font-medium text-red-700 hover:underline">
            {t('dashboard.alertBanner.show')}
          </Link>
        </div>
      )}

      {/* Otsikkorivi */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold text-gray-900">{t('dashboard.title')}</h1>
        <div className="flex items-center gap-2">
          <button onClick={load} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800">
            <RefreshCw size={14} />
            {t('dashboard.refresh')}
          </button>
        </div>
      </div>

      {/* Ryhmäsuodatin */}
      {groups.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <button
            onClick={() => { setSelectedGroup(null); setGroupShelterIds(null); }}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors
              ${selectedGroup === null ? 'bg-brand-700 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            {t('dashboard.allShelters')}
          </button>
          {groups.map(g => (
            <button
              key={g.id}
              onClick={() => setSelectedGroup(g.id)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors
                ${selectedGroup === g.id ? 'bg-brand-700 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              {g.name}
              <span className={`ml-1 ${selectedGroup === g.id ? 'text-white/70' : 'text-gray-400'}`}>
                {g.shelter_count}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Haku */}
      <div className="relative mb-4">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        <input
          type="search"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder={t('dashboard.searchPlaceholder')}
          className="w-full pl-9 pr-4 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:border-brand-600 focus:ring-1 focus:ring-brand-600"
        />
      </div>

      {loading && <div className="text-center py-16 text-gray-400 text-sm">{t('dashboard.loading')}</div>}
      {error && <div className="text-center py-16 text-red-500 text-sm">{error}</div>}
      {!loading && !error && filteredShelters.length === 0 && (
        <div className="text-center py-16 text-gray-400 text-sm">
          {query ? t('dashboard.noResults', { query }) : t('dashboard.noSheltersEmpty')}
        </div>
      )}

      <ShelterListView
        shelters={filteredShelters}
        groups={groups}
        selectedGroup={selectedGroup}
        unackedIds={unackedIds}
        onReload={load}
        canEdit={canEdit}
      />
    </div>
  );
}
